import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import api from "../api/client";
import type { CapabilityMap, Organization, Paginated } from "../types/api";
import { unwrapResults } from "../types/api";

const personalCapabilities: CapabilityMap = {
  canUsePersonalPlanner: true,
  canCreateOrganization: false,
  canInviteEmployees: false,
  canManageTeams: false,
  canRequestLeave: false,
  canApproveLeave: false,
  canViewTeamPlanning: false,
  canViewOrganizationReports: false,
  canManageOrganizationPolicies: false,
  canExportOrganizationData: false,
  canUseMultipleTeams: false,
  canUseMultipleManagers: false,
  canUseDepartments: false,
  canUseLocations: false,
  canUseExtendedAudit: false,
  organizationReadOnly: false,
};

type WorkspaceContextValue = {
  organizations: Organization[];
  organization: Organization | null;
  organizationId: number | null;
  capabilities: CapabilityMap;
  loading: boolean;
  selectOrganization: (id: number | null) => void;
  reload: () => Promise<void>;
};

const WorkspaceContext = createContext<WorkspaceContextValue | undefined>(undefined);

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [organizationId, setOrganizationId] = useState<number | null>(() => {
    const stored = window.localStorage.getItem("verlofkaart.organization");
    return stored ? Number(stored) : null;
  });
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const response = await api.get<Paginated<Organization> | Organization[]>(
        "/organizations/",
      );
      const rows = unwrapResults(response.data);
      setOrganizations(rows);
      setOrganizationId((current) =>
        current && rows.some((row) => row.id === current) ? current : null,
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const selectOrganization = useCallback((id: number | null) => {
    setOrganizationId(id);
    if (id) window.localStorage.setItem("verlofkaart.organization", String(id));
    else window.localStorage.removeItem("verlofkaart.organization");
  }, []);

  const organization = useMemo(
    () => organizations.find((row) => row.id === organizationId) ?? null,
    [organizationId, organizations],
  );

  const value = useMemo(
    () => ({
      organizations,
      organization,
      organizationId,
      capabilities: organization?.capabilities ?? personalCapabilities,
      loading,
      selectOrganization,
      reload,
    }),
    [organizations, organization, organizationId, loading, selectOrganization, reload],
  );
  return (
    <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (!context) throw new Error("useWorkspace must be used within WorkspaceProvider");
  return context;
}
